import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import React, { ReactNode, useEffect, useRef } from 'react';

/**
 * Renames our products in everything the user reads once a whitelabel config
 * is active: "Dieter" (and "simply Legal") become the partner's avatarName,
 * "Klaus" becomes its klausName, info@dieter-datenschutz.de becomes the
 * partner's support address. Text nodes only: attributes, hrefs and inputs
 * stay as they are.
 *
 * `global` watches the whole document instead of its own subtree, so modals,
 * sheets, snackbars and menus rendered through a portal are covered too; the
 * Root mounts one such instance. The non-global instances inside the app
 * predate it and are harmless duplicates.
 */
export const TextReplacer: React.FC<{ children: ReactNode; global?: boolean }> = ({ children, global = false }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { whitelabelConfig, whitelabelLoading } = useUserWithCompany();

  useEffect(() => {
    const target: Node | null = global ? document.body : ref.current;
    if (!target || !whitelabelConfig || whitelabelLoading) return;
    const replacement = whitelabelConfig.avatarName || 'Datenschutzprofi';
    const klausReplacement = whitelabelConfig.klausName || 'KI-Compliance-Tool';
    const supportMail = whitelabelConfig.supportEmail || whitelabelConfig.senderEmail || null;

    const replaceInTextNode = (node: Node): void => {
      if (!node.textContent) return;
      let replaced = node.textContent
        .replace(/simply Legal( GmbH)?/g, replacement)
        .replace(/Dieter macht den Datenschutz/g, replacement)
        .replace(/Dieter Datenschutz/g, replacement)
        .replace(/Dieter/g, replacement)
        .replace(/»DIETER«|DIETER/g, replacement)
        .replace(/Klaus kann KI/g, klausReplacement)
        .replace(/Klaus/g, klausReplacement);
      if (supportMail) replaced = replaced.replace(/info@dieter-datenschutz\.de/g, supportMail);
      // Only write back on change, so the observer doesn't loop on its own mutations
      if (replaced !== node.textContent) node.textContent = replaced;
    };

    const replaceText = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        replaceInTextNode(node);
      } else {
        node.childNodes.forEach((child) => replaceText(child));
      }
    };

    replaceText(target);

    // Content mounted or updated after the initial render (lazy-loaded data,
    // expanded accordions, chat messages) never re-renders this wrapper, so
    // watch the subtree instead of relying on re-renders.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          replaceInTextNode(mutation.target);
        }
        mutation.addedNodes.forEach((node) => replaceText(node));
      }
    });
    observer.observe(target, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [whitelabelConfig, whitelabelLoading, global]);

  return <div ref={ref}>{children}</div>;
};
